import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useAtomValue } from 'jotai';
import { Alert } from '@mui/material';
import { PageContainer } from '../../components/Layout/PageContainer';
import { ScheduleValue } from '../../schemas/SourcingList.schema';
import { SourcingGrid } from './SourcingGrid';
import { SourcingToolbar } from './SourcingToolbar';
import { SaveSearchDialog } from './SaveSearchDialog';
import {
  useAgentSearchResultsByIdQuery,
  useCreateSavedSearchMutation,
} from '../../services/queries-hooks/SourcingListsQueriesHooks';
import { sourcingGridApiAtom } from './state/SourcingListsState';
import { sourcingSearchPromptAtom } from './state/SourcingStateJ';
import { trackEvent } from '../../util/datadogTracking';

const pageContainerSx = { padding: '0 1.5rem 1rem', height: '100%' };

export function SourcingAgentQueryResultPage() {
  const { searchId } = useParams<{ searchId: string }>();
  const searchQuery = useAgentSearchResultsByIdQuery(searchId);

  // FD-3123: emit sourcing.search_run once per searchId, after the results have
  // loaded (and passed schema casting). Tracking here rather than in the queryFn
  // keeps an analytics "search" tied to the user action instead of react-query's
  // fetch mechanics, so refetch-on-focus doesn't inflate counts. A Set (not a
  // single slot) dedupes each searchId for the life of the mount: this route
  // element isn't keyed on :searchId, so navigating back to a previously-viewed
  // search must not re-emit. result_count is the post-cast set actually shown.
  const trackedSearchIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!searchId || !searchQuery.isSuccess || trackedSearchIdsRef.current.has(searchId)) {
      return;
    }
    trackedSearchIdsRef.current.add(searchId);
    trackEvent('sourcing.search_run', { result_count: searchQuery.data.length });
  }, [searchId, searchQuery.isSuccess, searchQuery.data]);

  const gridApi = useAtomValue(sourcingGridApiAtom);
  const prompt = useAtomValue(sourcingSearchPromptAtom);

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const createSavedSearch = useCreateSavedSearchMutation();
  const isSaving = createSavedSearch.isPending;

  const handleDownloadCsv = useCallback(() => {
    gridApi?.exportDataAsCsv({ fileName: 'sourcing-search-results.csv', allColumns: true });
  }, [gridApi]);

  const handleSaveDialogCancel = useCallback(() => {
    // Ignore cancel attempts while a save is in flight so the request can settle deterministically.
    if (isSaving) {
      return;
    }
    setSaveDialogOpen(false);
  }, [isSaving]);

  const handleSaveSearchConfirm = useCallback(
    (name: string, schedule: ScheduleValue) => {
      createSavedSearch.mutate(
        { name, entityType: 'company', query: prompt ?? undefined, schedule },
        {
          onSettled: () => {
            setSaveDialogOpen(false);
          },
        }
      );
    },
    [prompt, createSavedSearch]
  );

  const rowData = searchQuery.data ?? [];

  if (searchQuery.isError) {
    return (
      <PageContainer sx={pageContainerSx}>
        <Alert severity='error' sx={{ width: '100%', height: 'min-content' }}>
          Failed to load search results. Please try again.
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer sx={pageContainerSx}>
      <SourcingToolbar
        count={rowData.length}
        prompt={prompt}
        onDownloadCsv={handleDownloadCsv}
        onSaveSearch={() => setSaveDialogOpen(true)}
      />
      <SourcingGrid
        entityType='company'
        rowData={rowData}
        isLoading={searchQuery.isLoading}
        pagination
        paginationPageSize={500}
      />
      <SaveSearchDialog
        open={saveDialogOpen}
        defaultName={prompt}
        query={prompt}
        saving={isSaving}
        onCancel={handleSaveDialogCancel}
        onConfirm={handleSaveSearchConfirm}
      />
    </PageContainer>
  );
}
